// ---------------------------------------------------------------------------
// Client for AEMET OpenData, via our own Cloudflare Pages Functions proxy
// (functions/api/aemet/*.ts — holds the real API key server-side, never sent
// to the browser). Used by AemetFetchDialog.tsx to fill in a sub-basin's
// precipitation/temperature (real) and ETP (derived) series from the
// nearest weather station(s), instead of uploading/pasting a file by hand.
//
// `fetchDailyValues` below asks for the sub-basin's whole candidate list
// (main + backups) in ONE request per date window, via AEMET's per-station
// endpoint (`/estacion/{code1},{code2},...}` — it accepts several
// comma-separated codes and returns just those stations, confirmed against
// the real API). This used to go through `todasestaciones` instead (the
// whole country in one call, filtered client-side down to our candidates,
// capped at ~10 days/request) — switched away from it because the
// per-station endpoint allows a MUCH wider window (see
// `STATION_WINDOW_DAYS`) while returning a far smaller payload (a handful of
// stations instead of ~900), without the request count depending on
// candidate count either way (comma-joining keeps it at one request per
// window, same as `todasestaciones` did).
//
// LOCAL DEV ESCAPE HATCH: if `VITE_AEMET_API_KEY_DEV` (or, for testing
// parallel lanes locally, `VITE_AEMET_API_KEYS_DEV`, comma-separated) is set
// (put it in a gitignored `.env.local`, see `.env.local.example`) AND Vite
// is running in dev mode, requests go straight to AEMET from the browser
// (CORS-open, confirmed against the real API) instead of through
// `/api/aemet/*` — useful to try out a freshly-issued key before it's
// configured as a Cloudflare secret, without needing `wrangler pages dev`.
// `import.meta.env.DEV` is statically `false` in a production build
// (`vite build`), so this whole path is dead code there — but NEVER set
// this var when producing a build meant for deployment (the normal
// git-push-triggers-Cloudflare-build flow never has your local `.env.local`
// in the first place, so this only matters if you ever build+deploy from
// your own machine by hand).
// ---------------------------------------------------------------------------
import { aemetDailyValuesUrl, aemetInventoryUrl, fetchAemetJson } from "./aemetProtocol.ts";
import { hargreavesEto } from "../eto.ts";
import { haversineDistanceKm, type LatLon } from "../geo.ts";
import { addDaysIso, dayOfYear, daysBetweenIso } from "../isoDate.ts";
import { TranslatableError } from "../../i18n/translate.ts";
import type { TextKey } from "../../i18n/es.ts";

const DEV_API_KEYS: string[] = import.meta.env.DEV
  ? ((import.meta.env.VITE_AEMET_API_KEYS_DEV as string | undefined)
      ?.split(",")
      .map((k) => k.trim())
      .filter(Boolean) ??
    (import.meta.env.VITE_AEMET_API_KEY_DEV
      ? [import.meta.env.VITE_AEMET_API_KEY_DEV as string]
      : []))
  : [];

if (DEV_API_KEYS.length > 0) {
  console.warn(
    `[aemet] VITE_AEMET_API_KEY(S)_DEV is set (${DEV_API_KEYS.length} lane(s)) — calling AEMET directly from the browser, bypassing /api/aemet. Local dev only, see aemet.ts's header comment.`
  );
}

export interface AemetStation extends LatLon {
  code: string;
  name: string;
  altitudeM: number;
}

export interface StationCandidate extends AemetStation {
  distanceKm: number;
}

export interface DateRange {
  /** ISO yyyy-mm-dd, inclusive. */
  start: string;
  end: string;
}

export interface AemetDailyRecord {
  /** mm/day. NaN if missing for that station/day. */
  prec: number;
  /** °C. */
  tmed: number;
  tmax: number;
  tmin: number;
}

// ---------------------------------------------------------------------------
// Station inventory + nearest-station selection.
// ---------------------------------------------------------------------------

interface RawAemetStation {
  indicativo: string;
  nombre: string;
  latitud: string;
  longitud: string;
  altitud: string;
}

/**
 * AEMET encodes both latitude and longitude the same way: 2-digit degrees,
 * 2-digit minutes, 2-digit seconds, then a hemisphere letter — e.g.
 * "394924N" = 39°49'24"N, "025309E" = 2°53'09"E (verified against a real
 * inventory record for Escorca/Lluc, Mallorca, whose known coordinates
 * match). Not a general-purpose DMS parser — relies on this fixed 6-digit
 * width, which is all AEMET's own station network needs (Spain's longitude
 * never reaches 3-digit degrees).
 */
export function parseAemetCoordinate(raw: string): number {
  const deg = Number(raw.slice(0, 2));
  const min = Number(raw.slice(2, 4));
  const sec = Number(raw.slice(4, 6));
  const hemisphere = raw.slice(6);
  const value = deg + min / 60 + sec / 3600;
  return hemisphere === "S" || hemisphere === "W" ? -value : value;
}

/**
 * Maps one of our own Cloudflare Functions' stable, English, untranslated
 * error codes (`functions/api/aemet/*.ts`/`functions/api/rocio/cells.ts` —
 * see aemetProtocol.ts's own comment on why the server sends a code instead
 * of prose) to a real `TranslatableError` in whichever language the app is
 * set to. Exported for rocio.ts's own fetch wrapper, which hits the exact
 * same failure shape against a different endpoint. Falls back to
 * `fallbackKey` for a response body that isn't JSON, has no `error` field,
 * or carries a code this map doesn't know about (an older client talking to
 * a newer/older deployment) — never throws on a malformed error body itself.
 */
const FETCH_ERROR_KEYS: Record<string, TextKey> = {
  not_configured: "aemetFetch.notConfigured",
  rate_limited: "aemetFetch.rateLimited",
  upstream_error: "aemetFetch.upstreamError",
  unexpected_response: "aemetFetch.unexpectedResponse",
  not_found: "aemetFetch.notFound",
};

/**
 * How long a client-side fetch (to our own `/api/aemet/*`/`/api/rocio/*`
 * proxy, or straight to AEMET in the local-dev escape hatch) waits before
 * self-aborting. Real incident (2026-08-27, Duero scale — 1728 sub-basins):
 * plain `fetch()` has no default timeout, and none of these calls passed
 * anything but a user-cancel `AbortSignal` (never auto-firing) — a single
 * stalled request (dropped connection, unresponsive edge) left that
 * sub-basin's job stuck in "downloading"/"loading-stations" FOREVER, which
 * in turn blocked every other sub-basin queued behind it in the same
 * concurrent lane (aemetJobStore.ts's `startQueued`/ROCIO_CONCURRENCY) —
 * they never even got a chance to start, showing as plain "never
 * attempted" rather than a visible, retriable error. 30s comfortably
 * covers a real AEMET/ROCIO round trip (aemetProtocol.ts's own
 * server-side `TIMEOUT_MS` is 15s for the upstream leg alone) while still
 * being far short of "forever".
 */
const FETCH_TIMEOUT_MS = 30_000;

/**
 * Combines a caller's own `AbortSignal` (e.g. a user "Cancel" button) with
 * an automatic timeout — either firing aborts the fetch. A timeout's own
 * abort reason is a `DOMException` named "TimeoutError", NOT "AbortError"
 * — every existing `err.name === "AbortError"` check in this file/rocio.ts
 * (there specifically to let a genuine user-cancel skip retries/bubble up
 * immediately) correctly leaves a timeout to fall through to the NORMAL
 * retry/error path instead, with no changes needed there.
 */
export function withTimeout(signal: AbortSignal | undefined): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(FETCH_TIMEOUT_MS);
  return signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
}

export async function translatableFetchError(
  res: Response,
  fallbackKey: TextKey = "aemetFetch.upstreamError"
): Promise<TranslatableError> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  const key = (body?.error && FETCH_ERROR_KEYS[body.error]) || fallbackKey;
  return new TranslatableError(key, { status: res.status });
}

async function fetchStationInventoryRaw(): Promise<RawAemetStation[]> {
  if (DEV_API_KEYS.length > 0) {
    return fetchAemetJson(aemetInventoryUrl(DEV_API_KEYS[0])) as Promise<RawAemetStation[]>;
  }
  const res = await fetch("/api/aemet/stations", { signal: withTimeout(undefined) });
  if (!res.ok) throw await translatableFetchError(res);
  return res.json();
}

let laneCountPromise: Promise<number> | null = null;

/**
 * How many AEMET keys are configured, i.e. how many windows `fetchDailyValues`
 * can safely download in parallel (one lane per key, see below) — cached in
 * memory for the session, same pattern as `fetchStationInventory`. Falls back
 * to 1 lane on any error (misconfigured deployment, offline) so a fetch still
 * proceeds sequentially instead of failing outright just because this
 * secondary lookup couldn't complete.
 *
 * Real gap found by deep-review (2026-08-29): the fallback used to be
 * cached forever (`laneCountPromise ??=` never re-runs once resolved,
 * fallback or not) with no log — a misconfigured `/api/aemet/lanes` or a
 * transient failure right at startup silently downloaded N× slower for
 * the rest of the session, with no trace of why and no way to recover
 * short of a reload. Now logs the failure and clears the cache (same
 * "don't cache a bad outcome" fix `fetchStationInventory` already applies
 * to its own rejection) so the next call gets a real retry.
 */
export function fetchLaneCount(): Promise<number> {
  if (DEV_API_KEYS.length > 0) return Promise.resolve(DEV_API_KEYS.length);
  laneCountPromise ??= (async () => {
    try {
      const res = await fetch("/api/aemet/lanes", { signal: withTimeout(undefined) });
      if (!res.ok) throw new Error(`Proxy responded ${res.status}`);
      const body = (await res.json()) as { count: number };
      return body.count > 0 ? body.count : 1;
    } catch (err) {
      laneCountPromise = null;
      console.warn("[aemet] fetchLaneCount failed, falling back to 1 lane:", err);
      return 1;
    }
  })();
  return laneCountPromise;
}

let stationInventoryPromise: Promise<AemetStation[]> | null = null;

/**
 * Full station inventory (lat/lon/altitude included) — cached in memory for
 * the session, the network barely changes. `??=` caches whatever promise is
 * created on the FIRST call — if that fetch fails (network blip, transient
 * 5xx), the rejected promise itself would otherwise stay cached forever
 * (`??=` never re-assigns once the variable is non-null, rejected or not),
 * permanently breaking every AEMET-dependent feature for the rest of the
 * session with no way to recover short of a page reload — including the
 * "Retry" button on the error state this feeds (AemetFetchDialog.tsx),
 * which would just keep returning the same dead promise. Clearing the cache
 * on rejection lets the next call actually retry against the network.
 */
export function fetchStationInventory(): Promise<AemetStation[]> {
  stationInventoryPromise ??= fetchStationInventoryRaw()
    .then((raw) =>
      raw.map((s) => ({
        code: s.indicativo,
        name: s.nombre,
        lat: parseAemetCoordinate(s.latitud),
        lon: parseAemetCoordinate(s.longitud),
        altitudeM: Number(s.altitud),
      }))
    )
    .catch((err) => {
      stationInventoryPromise = null;
      throw err;
    });
  return stationInventoryPromise;
}

/** Closest stations to `point`, nearest first — capped to `limit` (default 5) within `maxKm` (default 100, generous so a sub-basin in a sparser area still gets candidates instead of none). */
export function nearestStations(
  point: LatLon,
  stations: AemetStation[],
  opts: { maxKm?: number; limit?: number } = {}
): StationCandidate[] {
  const maxKm = opts.maxKm ?? 100;
  const limit = opts.limit ?? 5;
  return stations
    .map((s) => ({ ...s, distanceKm: haversineDistanceKm(point, s) }))
    .filter((s) => s.distanceKm <= maxKm)
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, limit);
}

// ---------------------------------------------------------------------------
// Windowed historical download.
// ---------------------------------------------------------------------------

export type ExtendDirection = "back" | "forward";

/**
 * AemetBulkFetchDialog.tsx's "extend range" mode: the editable end of the
 * new window (the other end is fixed, contiguous with `forcedRange` — see
 * `fixedBound` below and the component's own comment on why). `todayEnd` is
 * `defaultRangeEnd()` (aemetJobStore.ts) — passed in rather than called
 * here to keep this function pure/deterministic (and avoid a circular
 * import, since aemetJobStore.ts already imports from this file).
 */
export function proposedFreeBound(
  forcedRange: DateRange,
  direction: ExtendDirection,
  todayEnd: string
): string {
  if (direction === "forward") return todayEnd;
  const span = daysBetweenIso(forcedRange.start, forcedRange.end);
  return addDaysIso(forcedRange.start, -1 - span);
}

/** The non-editable end of "extend range" mode's new window — always exactly contiguous with `forcedRange`, so there's no way to leave a real calendar gap between old and new dates. */
export function fixedBound(forcedRange: DateRange, direction: ExtendDirection): string {
  return direction === "forward"
    ? addDaysIso(forcedRange.end, 1)
    : addDaysIso(forcedRange.start, -1);
}

/**
 * AEMET's real per-request limit for `/valores/climatologicos/diarios/datos/.../estacion/{codes}`
 * is NOT documented anywhere (not the FAQ, not the OpenAPI spec) — confirmed
 * by testing directly against the live API: a request spanning Jan 1 - Jul 6
 * (186 calendar days inclusive) succeeds, Jan 1 - Jul 7 (187 days) fails with
 * `{"descripcion": "El rango de fechas no puede ser superior a 6 meses", "estado": 404}`.
 * The error message undersells it slightly (186 days is a bit over 6
 * calendar months) — this constant stays a little under that real boundary
 * as a safety margin, same value `functions/api/aemet/daily.ts` enforces
 * server-side.
 */
const STATION_WINDOW_DAYS = 180;

/** Splits `range` into consecutive windows of at most `maxDays` days (default `STATION_WINDOW_DAYS`, AEMET's real per-station-request limit). */
export function enumerateWindows(range: DateRange, maxDays = STATION_WINDOW_DAYS): DateRange[] {
  const windows: DateRange[] = [];
  let start = range.start;
  while (start <= range.end) {
    const end = addDaysIso(start, maxDays - 1);
    const clampedEnd = end > range.end ? range.end : end;
    windows.push({ start, end: clampedEnd });
    start = addDaysIso(clampedEnd, 1);
  }
  return windows;
}

/**
 * AEMET quirks in the raw strings: decimals use a comma ("12,3"), and trace
 * precipitation too small to measure is written "Ip" ("inapreciable") rather
 * than a number — treated as 0 (real, negligible rain), not as missing.
 * Empty/undefined stays NaN (a real gap, same code the rest of the app
 * already uses for missing values, see calculateSeriesStats in dailySeries.ts).
 */
export function parseAemetNumber(raw: string | undefined): number {
  if (raw === undefined || raw === "") return NaN;
  if (raw.trim().toLowerCase() === "ip") return 0;
  const n = Number(raw.replace(",", "."));
  return Number.isNaN(n) ? NaN : n;
}

interface RawAemetDailyRecord {
  indicativo: string;
  fecha: string;
  prec?: string;
  tmed?: string;
  tmax?: string;
  tmin?: string;
}

export interface FetchDailyValuesResult {
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>;
  /** Windows that failed even after retries — reported to the user as gaps, not silently dropped. */
  failedWindows: DateRange[];
}

// Spacing between our own calls to /api/aemet/daily — each one costs AEMET
// 2 requests (our proxy's two-step fetch), and the 40 req/min budget (AEMET's
// official FAQ: "límite individual general para toda la API de 40 consultas
// por minuto") is shared by every visitor using this app's key(s), so this
// stays deliberately conservative rather than chasing the theoretical
// maximum.
export const WINDOW_SPACING_MS = 3000;
const MAX_RETRIES_PER_WINDOW = 2;

/** Exported for aemetJobStore.ts's own ROCIO pacing/retry (rate-limit backoff) — same abortable setTimeout wrapper, no reason to duplicate it. */
export function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Cancelado", "AbortError"));
      return;
    }
    const id = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(id);
      reject(new DOMException("Cancelado", "AbortError"));
    });
  });
}

/** `lane` picks which key to use — in dev mode indexes `DEV_API_KEYS` directly; through the proxy it's just forwarded as `&lane=N`, which `daily.ts` reduces mod its own key count (may differ from `DEV_API_KEYS.length`, so no mod here). */
async function fetchWindowRaw(
  w: DateRange,
  stationCodes: string[],
  lane: number,
  signal?: AbortSignal
): Promise<RawAemetDailyRecord[]> {
  if (DEV_API_KEYS.length > 0) {
    const key = DEV_API_KEYS[lane % DEV_API_KEYS.length];
    return fetchAemetJson(aemetDailyValuesUrl(w.start, w.end, key, stationCodes), {
      signal: withTimeout(signal),
    }) as Promise<RawAemetDailyRecord[]>;
  }
  const stations = stationCodes.map(encodeURIComponent).join(",");
  const res = await fetch(
    `/api/aemet/daily?start=${w.start}&end=${w.end}&stations=${stations}&lane=${lane}`,
    { signal: withTimeout(signal) }
  );
  if (!res.ok) throw new Error(`Proxy responded ${res.status}`);
  return res.json();
}

async function fetchWindowWithRetry(
  w: DateRange,
  stationCodes: string[],
  lane: number,
  signal?: AbortSignal
): Promise<RawAemetDailyRecord[] | null> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES_PER_WINDOW; attempt++) {
    try {
      return await fetchWindowRaw(w, stationCodes, lane, signal);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      lastErr = err;
    }
    if (attempt < MAX_RETRIES_PER_WINDOW) await delay(1500 * (attempt + 1), signal);
  }
  // Real gap found by deep-review (2026-08-29): every non-abort failure
  // (network, 5xx, timeout, malformed JSON, proxy error) used to vanish
  // here with zero trace — the caller only ever sees "N windows failed"
  // and a Retry button, with no way to tell why short of reproducing it.
  console.warn(
    `[aemet] window ${w.start}..${w.end} (lane ${lane}) failed after ${MAX_RETRIES_PER_WINDOW + 1} attempt(s):`,
    lastErr
  );
  return null;
}

// ---------------------------------------------------------------------------
// Cross-job station/window cache — a batch import (MITECO wizard, "download/
// extend all", AemetBulkFetchDialog.tsx) queues one job per sub-basin, each
// asking for its own ~5 nearest candidate stations (`nearestStations`), and
// neighboring sub-basins very often share several of those candidates.
// `AemetQueuedRequest`'s own callers always give every job in the SAME batch
// the exact same date range, so `enumerateWindows` produces byte-for-byte
// identical window boundaries across the whole batch — meaning a station a
// LATER job also wants, for a window an EARLIER job already fetched, doesn't
// need asking AEMET again. Module-level and session-scoped (cleared only on
// a page reload), same lifetime/spirit as `fetchStationInventory`'s cache
// above — realistic size stays small (Spain has ~900 stations total, and a
// window's worth of data per station is a few hundred small numbers).
// ---------------------------------------------------------------------------

const dailyValuesCache = new Map<string, Map<string, AemetDailyRecord>>();

function windowStationCacheKey(w: DateRange, stationCode: string): string {
  return `${w.start}_${w.end}|${stationCode}`;
}

/** Pure split of `stationCodes` into what's already cached for this exact window vs. what still needs asking AEMET — its own function so it's testable without touching the cache/network. */
export function partitionCachedStations(
  w: DateRange,
  stationCodes: string[],
  isCached: (key: string) => boolean
): { cached: string[]; missing: string[] } {
  const cached: string[] = [];
  const missing: string[] = [];
  for (const code of stationCodes) {
    (isCached(windowStationCacheKey(w, code)) ? cached : missing).push(code);
  }
  return { cached, missing };
}

/**
 * `fetchWindowWithRetry`, but every (window, station) pair already sitting
 * in `dailyValuesCache` — fetched by ANY earlier call this session, not just
 * ones for this same job — is reused instead of asked for again; only
 * `stationCodes` actually missing from the cache get a real request, and a
 * window where every requested station is already cached makes no network
 * call at all. `usedNetwork: false` tells the caller it's safe to skip the
 * inter-window pacing delay too — that delay exists to respect AEMET's real
 * rate limit, which a cache hit never touches.
 *
 * Returns `data: null` only if the still-missing stations' fetch failed AND
 * at least one requested station still has no cached data either — matches
 * `fetchWindowWithRetry`'s old all-or-nothing failure shape on purpose (see
 * `retryFailedWindows`'s own comment on why a silent partial gap would be
 * worse than a window flagged failed and retriable): a station this job
 * happens to already have cached is still returned even on that failure,
 * but if EVERY station is already cached, failure can't happen at all.
 */
async function fetchWindowCached(
  w: DateRange,
  stationCodes: string[],
  lane: number,
  signal?: AbortSignal
): Promise<{ data: Map<string, Map<string, AemetDailyRecord>> | null; usedNetwork: boolean }> {
  const { missing } = partitionCachedStations(w, stationCodes, (key) => dailyValuesCache.has(key));
  let fetchFailed = false;
  if (missing.length > 0) {
    const fetched = await fetchWindowWithRetry(w, missing, lane, signal);
    if (fetched === null) {
      fetchFailed = true;
    } else {
      // Every station asked for gets a (possibly empty) cache entry — a
      // station with zero rows that window (retired, not yet installed) is a
      // real, cacheable fact, not "not yet tried".
      for (const code of missing) dailyValuesCache.set(windowStationCacheKey(w, code), new Map());
      for (const r of fetched) {
        dailyValuesCache.get(windowStationCacheKey(w, r.indicativo))?.set(r.fecha, {
          prec: parseAemetNumber(r.prec),
          tmed: parseAemetNumber(r.tmed),
          tmax: parseAemetNumber(r.tmax),
          tmin: parseAemetNumber(r.tmin),
        });
      }
    }
  }

  const data = new Map<string, Map<string, AemetDailyRecord>>();
  let anyStillMissing = false;
  for (const code of stationCodes) {
    const cached = dailyValuesCache.get(windowStationCacheKey(w, code));
    if (cached) data.set(code, cached);
    else anyStillMissing = true;
  }
  return {
    data: anyStillMissing && fetchFailed ? null : data,
    usedNetwork: missing.length > 0,
  };
}

/**
 * Downloads daily values for an explicit list of `windows`, for all of
 * `stationCodes` at once (one request per window covers every candidate —
 * see the module header comment and `STATION_WINDOW_DAYS`). A window that
 * still fails after retries is recorded in `failedWindows` and the download
 * continues — one bad window doesn't abort a fetch that can otherwise take
 * many minutes. Split out from `fetchDailyValues` so a later retry can
 * re-run just the windows that failed the first time, instead of the whole
 * date range again (see `aemetJobStore.ts`'s `retryFailedWindows`).
 *
 * `opts.laneCount` (default 1) splits the windows round-robin across that
 * many concurrent "lanes" (see `fetchLaneCount`/`resolveApiKeys`), each lane
 * using its own AEMET key and pacing its own consecutive requests by
 * `WINDOW_SPACING_MS` — N lanes give up to Nx real throughput without any
 * single key seeing more than its own sequential rate. Merging into the
 * shared `byStationByDate`/`failedWindows`/progress counters from several
 * concurrent lanes is safe: JS is single-threaded, so each `await` point is
 * the only place another lane's code can interleave, and none of the merge
 * steps below straddle an `await`.
 */
export async function fetchDailyValuesForWindows(
  windows: DateRange[],
  stationCodes: string[],
  opts: {
    onProgress?: (done: number, total: number) => void;
    signal?: AbortSignal;
    laneCount?: number;
  } = {}
): Promise<FetchDailyValuesResult> {
  const byStationByDate = new Map<string, Map<string, AemetDailyRecord>>();
  const failedWindows: DateRange[] = [];
  const laneCount = Math.max(1, opts.laneCount ?? 1);
  let done = 0;

  async function runLane(lane: number): Promise<void> {
    for (let i = lane; i < windows.length; i += laneCount) {
      const w = windows[i];
      const { data, usedNetwork } = await fetchWindowCached(w, stationCodes, lane, opts.signal);
      if (data === null) {
        failedWindows.push(w);
      } else {
        for (const [station, byDate] of data) {
          let existing = byStationByDate.get(station);
          if (!existing) {
            existing = new Map();
            byStationByDate.set(station, existing);
          }
          for (const [date, record] of byDate) existing.set(date, record);
        }
      }
      done++;
      opts.onProgress?.(done, windows.length);
      const isLanesLastWindow = i + laneCount >= windows.length;
      if (!isLanesLastWindow && usedNetwork) await delay(WINDOW_SPACING_MS, opts.signal);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(laneCount, windows.length) }, (_, lane) => runLane(lane))
  );

  return { byStationByDate, failedWindows };
}

/** Splits `range` into windows (`enumerateWindows`) and downloads all of them — see `fetchDailyValuesForWindows` for the actual download logic. */
export function fetchDailyValues(
  range: DateRange,
  stationCodes: string[],
  opts: {
    onProgress?: (done: number, total: number) => void;
    signal?: AbortSignal;
    laneCount?: number;
  } = {}
): Promise<FetchDailyValuesResult> {
  return fetchDailyValuesForWindows(enumerateWindows(range), stationCodes, opts);
}

/**
 * Merges a retry's result (`fetchDailyValuesForWindows`, called again with
 * only `prev.failedWindows`) back into the original download: newly-fetched
 * records fill in/overwrite `byStationByDate`, and `failedWindows` becomes
 * whatever the retry itself still couldn't get — a strict subset of
 * `prev.failedWindows` (the only windows a retry ever asks for), never the
 * union of both attempts' failures.
 */
export function mergeDailyValuesResults(
  prev: FetchDailyValuesResult,
  retry: FetchDailyValuesResult
): FetchDailyValuesResult {
  const byStationByDate = new Map(prev.byStationByDate);
  for (const [station, byDate] of retry.byStationByDate) {
    const merged = new Map(byStationByDate.get(station) ?? []);
    for (const [date, record] of byDate) merged.set(date, record);
    byStationByDate.set(station, merged);
  }
  return { byStationByDate, failedWindows: retry.failedWindows };
}

// ---------------------------------------------------------------------------
// Fallback-chain series building — for each day, use the nearest candidate
// station that actually has a value, so a single station's outage doesn't
// leave a real gap the other nearby stations could have covered.
// ---------------------------------------------------------------------------

/** Exported for rocio.ts's own mean/min/max-based series builders — same shape, ROCIO isn't a "station" but reuses this exact contract (`sourceByDate` keyed the same way). */
export interface BuiltSeries {
  values: number[];
  /** Which station code covered each date, or null if none of the candidates had data that day. */
  sourceByDate: Map<string, string | null>;
}

export function buildSeriesFromStations(
  candidateCodesInPriorityOrder: string[],
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>,
  dates: string[],
  variable: "prec" | "tmed"
): BuiltSeries {
  const sourceByDate = new Map<string, string | null>();
  const values = dates.map((date) => {
    for (const code of candidateCodesInPriorityOrder) {
      const value = byStationByDate.get(code)?.get(date)?.[variable];
      if (value !== undefined && !Number.isNaN(value)) {
        sourceByDate.set(date, code);
        return value;
      }
    }
    sourceByDate.set(date, null);
    return NaN;
  });
  return { values, sourceByDate };
}

/** Same fallback-chain idea as `buildSeriesFromStations`, but for ETP: a day only counts as covered if the chosen station has tmed AND tmax AND tmin (Hargreaves needs all three), even if a "worse" candidate would have covered tmed alone. */
export function buildEtoSeries(
  candidateCodesInPriorityOrder: string[],
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>,
  dates: string[],
  latDeg: number
): BuiltSeries {
  const sourceByDate = new Map<string, string | null>();
  const values = dates.map((date) => {
    for (const code of candidateCodesInPriorityOrder) {
      const record = byStationByDate.get(code)?.get(date);
      if (!record) continue;
      const { tmed, tmax, tmin } = record;
      if (Number.isNaN(tmed) || Number.isNaN(tmax) || Number.isNaN(tmin)) continue;
      sourceByDate.set(date, code);
      return hargreavesEto(tmax, tmin, tmed, latDeg, dayOfYear(date));
    }
    sourceByDate.set(date, null);
    return NaN;
  });
  return { values, sourceByDate };
}

// ---------------------------------------------------------------------------
// IDW (Inverse Distance Weighting) series building — an alternative to the
// fallback-chain above: instead of picking ONE candidate per day (nearest
// first), blends EVERY candidate that has a value that day, weighted by
// 1/distance² (the standard IDW exponent in the hydrology literature).
// Offered as a second method next to "nearest station, fill from the next
// one" (AemetFetchDialog.tsx/AemetBulkFetchDialog.tsx's method dropdown) —
// neither is objectively "the" right choice: nearest-with-fallback is
// simpler and fine when the primary station is genuinely close and the
// terrain is homogeneous, while IDW smooths across several stations and
// tends to do better when the nearest one is farther away or the terrain is
// more complex (mountainous basins, sparse networks) — see the research
// summary this was built from (comparative studies consistently rank IDW
// above plain nearest-neighbor once the network isn't dense/flat, though
// clearly below altitude-aware methods like kriging-with-elevation, which
// would need a DEM this app doesn't have).
// ---------------------------------------------------------------------------

/** "rocio" is handled almost entirely OUTSIDE this file — see rocio.ts's header comment for why (a different data source, R2-backed instead of AEMET's live API) — it's part of this union purely so every existing consumer of `StationSelectionMethod` (the method dropdown, aemetJobStore.ts's `AemetJob.method`) already type-checks against it without a parallel type. */
export type StationSelectionMethod = "nearest" | "idw" | "rocio";

/** `sourceByDate` label for every date IDW actually combined — never a real station code (no station is ever named literally "IDW"), so `AemetProvenanceDialog.tsx`'s `stationName()` lookup naturally falls through to displaying it verbatim instead of a station name. */
export const IDW_SOURCE_LABEL = "IDW";

/**
 * Only the fields IDW weighting actually reads from a candidate — narrower
 * than the full `StationCandidate` (which also carries `name`/`lat`/`lon`/
 * `altitudeM`) so a caller that only has this much can still use it without
 * fabricating fake geometry. In practice that's `aemetRetry.ts`'s cold
 * retry: `AemetProvenance.stationsUsed` (storage.ts) only ever persists
 * `code`/`name`/`distanceKm`, never a station's coordinates, since nothing
 * else needed them until now.
 */
interface IdwCandidate {
  code: string;
  distanceKm: number;
}

/**
 * Weighted average of `variable` across every candidate with a valid value
 * for `date`, weight = 1/distanceKm² — `NaN` if none of them have data that
 * day. A candidate at distance 0 (the sub-basin's centroid coincides with a
 * station) short-circuits to that station's own value, since 1/0² would
 * otherwise blow up the weighting.
 */
function idwAverage(
  candidates: IdwCandidate[],
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>,
  date: string,
  variable: keyof AemetDailyRecord
): number {
  let weightedSum = 0;
  let weightSum = 0;
  for (const c of candidates) {
    const value = byStationByDate.get(c.code)?.get(date)?.[variable];
    if (value === undefined || Number.isNaN(value)) continue;
    if (c.distanceKm === 0) return value;
    const weight = 1 / (c.distanceKm * c.distanceKm);
    weightedSum += weight * value;
    weightSum += weight;
  }
  return weightSum > 0 ? weightedSum / weightSum : NaN;
}

/**
 * IDW counterpart to `buildSeriesFromStations` — same signature shape, but
 * takes `IdwCandidate[]` (needs `distanceKm`) instead of just codes, since
 * every candidate can contribute every day instead of only the first
 * available one. `sourceLabel` defaults to `IDW_SOURCE_LABEL`.
 */
export function buildSeriesFromStationsIdw(
  candidates: IdwCandidate[],
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>,
  dates: string[],
  variable: "prec" | "tmed",
  sourceLabel: string = IDW_SOURCE_LABEL
): BuiltSeries {
  const sourceByDate = new Map<string, string | null>();
  const values = dates.map((date) => {
    const value = idwAverage(candidates, byStationByDate, date, variable);
    sourceByDate.set(date, Number.isNaN(value) ? null : sourceLabel);
    return value;
  });
  return { values, sourceByDate };
}

/** IDW counterpart to `buildEtoSeries` — tmed/tmax/tmin are each IDW-blended independently (a station missing just tmax that day still contributes tmed/tmin), then Hargreaves runs once on the combined trio; a day only counts as covered if all three ended up with a real blended value. */
export function buildEtoSeriesIdw(
  candidates: IdwCandidate[],
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>,
  dates: string[],
  latDeg: number
): BuiltSeries {
  const sourceByDate = new Map<string, string | null>();
  const values = dates.map((date) => {
    const tmed = idwAverage(candidates, byStationByDate, date, "tmed");
    const tmax = idwAverage(candidates, byStationByDate, date, "tmax");
    const tmin = idwAverage(candidates, byStationByDate, date, "tmin");
    if (Number.isNaN(tmed) || Number.isNaN(tmax) || Number.isNaN(tmin)) {
      sourceByDate.set(date, null);
      return NaN;
    }
    sourceByDate.set(date, IDW_SOURCE_LABEL);
    return hargreavesEto(tmax, tmin, tmed, latDeg, dayOfYear(date));
  });
  return { values, sourceByDate };
}

/**
 * Groups `sourceByDate` into contiguous same-source runs (station code, or
 * `null` for no data), in the order `dates` lists them — a raw per-day tally
 * says "60 days from station A" but not WHEN; over a multi-year fetch that
 * count stops meaning anything ("10950 days") while the concrete ranges
 * still do, and it's the only way to show exactly which dates fell back to
 * a backup station or had no data at all.
 * `dates` must be the same ordered list `sourceByDate` was built against
 * (AemetProvenanceDialog.tsx/AemetFetchDialog.tsx regenerate it via
 * `enumerateWindows`, assuming no calendar-day gaps — true for any daily
 * series this app reads or writes).
 */
export function sourceRanges(
  sourceByDate: Map<string, string | null>,
  dates: string[]
): Map<string | null, DateRange[]> {
  const ranges = new Map<string | null, DateRange[]>();
  let currentSource: string | null | undefined;
  let start: string | null = null;
  let prev: string | null = null;
  function flush() {
    if (currentSource === undefined || start === null || prev === null) return;
    const list = ranges.get(currentSource) ?? [];
    list.push({ start, end: prev });
    ranges.set(currentSource, list);
  }
  for (const date of dates) {
    const source = sourceByDate.get(date) ?? null;
    if (source !== currentSource) {
      flush();
      currentSource = source;
      start = date;
    }
    prev = date;
  }
  flush();
  return ranges;
}

/** The no-data ranges from `sourceRanges` alone — shorthand for callers that only care about gaps, not which station covered the rest. */
export function gapRanges(sourceByDate: Map<string, string | null>, dates: string[]): DateRange[] {
  return sourceRanges(sourceByDate, dates).get(null) ?? [];
}

/**
 * The exact inverse of `sourceRanges` — walks each source's ranges back out
 * into a per-day `Map<date, source>`. Only needed where something has to
 * fold a FRESH per-day fetch into an already-stored, already-grouped
 * `AemetProvenance.sourceByDate` (`mergeSourceByDate`, aemetApply.ts): the
 * merge itself is naturally per-day (a fresh fetch can overwrite individual
 * days one at a time), so the stored side has to come back to that shape
 * first, then get re-grouped via `sourceRanges` once the merge is done.
 * Never called for a whole batch at once (see `AemetProvenance`'s own
 * comment on the Duero-scale incident this shape avoids) — only for the one
 * sub-basin actually being extended/retried.
 */
export function expandSourceRanges(
  bySource: Map<string | null, DateRange[]>
): Map<string, string | null> {
  const sourceByDate = new Map<string, string | null>();
  for (const [source, ranges] of bySource) {
    for (const range of ranges) {
      for (let date = range.start; date <= range.end; date = addDaysIso(date, 1)) {
        sourceByDate.set(date, source);
      }
    }
  }
  return sourceByDate;
}
